import { API_URL } from '../config';

export class YouTubeService {
  // Cache storage with expiration
  static cache = new Map();
  static CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

  /**
   * Get cached data or return null if expired
   */
  static getCachedData(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > this.CACHE_DURATION) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  /**
   * Set data in cache with timestamp
   */
  static setCachedData(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Clear all cached data
   */
  static clearCache() {
    this.cache.clear();
    console.log('YouTube service cache cleared');
  }

  /**
   * Get cache age in human-readable format
   */
  static getCacheAge(key) {
    const cached = this.cache.get(key);
    if (!cached) return 'unknown';
    
    const ageMs = Date.now() - cached.timestamp;
    const ageMinutes = Math.floor(ageMs / (1000 * 60));
    const ageHours = Math.floor(ageMs / (1000 * 60 * 60));
    
    if (ageMinutes < 1) return 'just now';
    if (ageMinutes < 60) return `${ageMinutes}m ago`;
    if (ageHours < 24) return `${ageHours}h ago`;
    
    const ageDays = Math.floor(ageHours / 24);
    return `${ageDays}d ago`;
  }

  /**
   * Get cache statistics
   */
  static getCacheStats() {
    const now = Date.now();
    const stats = {
      totalEntries: this.cache.size,
      expiredEntries: 0,
      validEntries: 0
    };

    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > this.CACHE_DURATION) {
        stats.expiredEntries++;
      } else {
        stats.validEntries++;
      }
    }

    return stats;
  }



  /**
   * Get the latest video from YouTube
   */
  static async getLatestVideo() {
    const cacheKey = 'latest-video';
    const cached = this.getCachedData(cacheKey);

    try {
      const response = await fetch(`${API_URL}/youtube/latest-video`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch latest video');
      }
      
      // Cache the successful response
      this.setCachedData(cacheKey, data.data);
      return data.data;
    } catch (error) {
      console.error('❌ Error fetching latest YouTube video from API:', error.message);
      
      // Fallback to cached data if available
      if (cached) {
        console.log('🔄 API unavailable - Using cached latest video data (age: ' + this.getCacheAge(cacheKey) + ')');
        return cached;
      }
      
      console.log('⚠️ No cached data available for latest video');
      return null;
    }
  }

  /**
   * Get recent videos from YouTube
   */
  static async getRecentVideos(maxResults = 10) {
    const cacheKey = `recent-videos-${maxResults}`;
    const cached = this.getCachedData(cacheKey);

    try {
      const response = await fetch(`${API_URL}/youtube/recent-videos?maxResults=${maxResults}`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch recent videos');
      }
      
      // Cache the successful response
      this.setCachedData(cacheKey, data.data);
      return data.data;
    } catch (error) {
      console.error('❌ Error fetching recent YouTube videos from API:', error.message);
      
      // Fallback to cached data if available
      if (cached) {
        console.log('🔄 API unavailable - Using cached recent videos data (age: ' + this.getCacheAge(cacheKey) + ', videos: ' + cached.length + ')');
        return cached;
      }
      
      console.log('⚠️ No cached data available for recent videos');
      return [];
    }
  }

  /**
   * Get YouTube channel information
   */
  static async getChannelInfo() {
    const cacheKey = 'channel-info';
    const cached = this.getCachedData(cacheKey);

    try {
      const response = await fetch(`${API_URL}/youtube/channel-info`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch channel info');
      }
      
      // Cache the successful response
      this.setCachedData(cacheKey, data.data);
      return data.data;
    } catch (error) {
      console.error('❌ Error fetching YouTube channel info from API:', error.message);
      
      // Fallback to cached data if available
      if (cached) {
        console.log('🔄 API unavailable - Using cached channel info data (age: ' + this.getCacheAge(cacheKey) + ')');
        return cached;
      }
      
      console.log('⚠️ No cached data available for channel info');
      return null;
    }
  }

  /**
   * Get YouTube service status
   */
  static async getServiceStatus() {
    try {
      const response = await fetch(`${API_URL}/youtube/status`);
      const data = await response.json();
      
      return data;
    } catch (error) {
      console.error('Error fetching YouTube service status:', error);
      return {
        configured: false,
        message: 'Service unavailable'
      };
    }
  }

  /**
   * Get YouTube sync status
   */
  static async getSyncStatus() {
    try {
      const response = await fetch(`${API_URL}/youtube/sync-status`);
      const data = await response.json();
      
      return data;
    } catch (error) {
      console.error('Error fetching YouTube sync status:', error);
      return {
        configured: false,
        message: 'Service unavailable'
      };
    }
  }

  /**
   * Trigger manual YouTube sync (requires authentication)
   */
  static async triggerManualSync(token) {
    try {
      const response = await fetch(`${API_URL}/youtube/manual-sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to trigger manual sync');
      }
      
      return data;
    } catch (error) {
      console.error('Error triggering manual YouTube sync:', error);
      throw error;
    }
  }

  /**
   * Generate YouTube thumbnail URL
   */
  static getThumbnailUrl(videoId, quality = 'maxresdefault') {
    if (!videoId) return null;
    
    const qualities = {
      maxresdefault: 'maxresdefault.jpg',
      hqdefault: 'hqdefault.jpg',
      mqdefault: 'mqdefault.jpg',
      sddefault: 'sddefault.jpg',
      default: 'default.jpg'
    };
    
    const qualityKey = qualities[quality] || qualities.maxresdefault;
    return `https://img.youtube.com/vi/${videoId}/${qualityKey}`;
  }

  /**
   * Generate YouTube video URL
   */
  static getVideoUrl(videoId) {
    if (!videoId) return null;
    return `https://www.youtube.com/watch?v=${videoId}`;
  }

  /**
   * Generate YouTube embed URL
   */
  static getEmbedUrl(videoId) {
    if (!videoId) return null;
    return `https://www.youtube.com/embed/${videoId}`;
  }

  /**
   * Format video duration from ISO 8601 to readable format
   */
  static formatDuration(duration) {
    if (!duration) return 'Unknown';
    
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
    if (!match) return duration;

    const hours = (match[1] || '').replace('H', '');
    const minutes = (match[2] || '').replace('M', '');
    const seconds = (match[3] || '').replace('S', '');

    let result = '';
    if (hours) result += `${hours}h `;
    if (minutes) result += `${minutes}m `;
    if (seconds && !hours && !minutes) result += `${seconds}s`;

    return result.trim();
  }

  /**
   * Format view count with appropriate suffixes
   */
  static formatViewCount(viewCount) {
    if (!viewCount) return '0';
    
    const count = parseInt(viewCount, 10);
    if (isNaN(count)) return viewCount;
    
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    
    return count.toString();
  }

  /**
   * Format published date
   */
  static formatPublishedDate(publishedAt) {
    if (!publishedAt) return 'Unknown';
    
    const date = new Date(publishedAt);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }
}
